import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { BaseButton } from './styled/BaseButton';
import { Shield, Sparkles, Ticket } from 'lucide-react';
import { ethers } from 'ethers';
import CONTRACTS from '../config/contracts';
import { LotteryCoreABI } from '../contracts/abis/modules';
import { TARGET_NETWORK } from '../config/network';
import { formatEth } from '../utils/format';
import { TrustBadges } from './TrustBadges';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  text-align: center;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 25px;
  position: relative;
  /* Avoid nested scrolling; let the page handle scroll */
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 25px;
    background: ${({ theme }) => theme.gradients.cardBorder};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 25px;
    background: ${({ theme }) => theme.gradients.mesh};
    opacity: 0.45;
    pointer-events: none;
  }
`;

const Kicker = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.glass};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 999px;
  padding: 0.5rem 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.45rem 0.75rem;
    letter-spacing: 1.6px;
  }
`;

const ScrollableContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  animation: ${shimmer} 2s ease-out;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 28px 16px;
    gap: 1.5rem;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: clamp(2.25rem, 4vw, 4rem);
  font-weight: 800;
  margin: 0.75rem 0 0.5rem;
  background: ${({ theme }) => theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 24px rgba(0, 208, 192, 0.25);
`;

const WelcomeDescription = styled.p`
  font-size: clamp(1.05rem, 1.5vw, 1.35rem);
  max-width: 800px;
  margin: 0 auto 1.25rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.9;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.25rem;
  width: 100%;
  padding: 1rem 0;
`;

const FeatureCard = styled.div<{ delay: number }>`
  background: ${({ theme }) => theme.colors.glass};
  border-radius: 20px;
  padding: 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInScale} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: ${props => props.delay}s;
  opacity: 0;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 0 1rem;
  }

  &:hover {
    transform: translateY(-5px) scale(1.02);
    background: ${({ theme }) => theme.colors.glassHover};
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
    border-color: rgba(0, 208, 192, 0.25);
  }
`;

const FeatureIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  background: rgba(0, 208, 192, 0.12);
  border: 1px solid rgba(0, 208, 192, 0.18);
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const FeatureTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.8;
  line-height: 1.5;
`;

const ActionButton = styled(BaseButton)`
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 900px;
  margin: 0.25rem auto 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.glass};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 16px;
  padding: 1rem 1.25rem;
  text-align: center;

  .label {
    color: rgba(255,255,255,0.7);
    font-size: 0.9rem;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .value {
    font-size: 1.4rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.gold};
  }
`;

interface ModalProps {
  $isVisible: boolean;
}

const ModalOverlay = styled.div<ModalProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
  transition: all 0.3s ease-in-out;
  z-index: 1000;
  padding: 20px;
`;

// Lightweight centered panel for Learn More
const InfoDrawer = styled.aside<ModalProps>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${props => (props.$isVisible ? '1' : '0.98')});
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  pointer-events: ${props => (props.$isVisible ? 'auto' : 'none')};
  width: 720px;
  max-width: 95%;
  max-height: 85vh;
  background: rgba(10, 10, 10, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out;

  @media (max-width: 768px) {
    width: calc(100% - 24px);
    max-height: 80vh;
  }
`;

const DrawerHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 0.75rem 1rem 0.5rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
`;

const DrawerTitle = styled.h2`
  font-size: 1.1rem;
  margin: 0;
  background: linear-gradient(45deg, #00ffcc, #33ccff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`;

const DrawerBody = styled.div`
  padding: 0.75rem 1rem 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  min-height: 0; /* enable flexbox child scrolling */
  flex: 1;
  overflow: hidden;
`;

const HeaderSpacer = styled.div`
  width: 40px;
  height: 40px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  padding: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    transform: scale(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    &::before {
      transform: scale(1);
    }
    color: white;
  }

  span {
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    transform: translateY(-1px);
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.75rem 0.25rem 0.5rem;
  background: rgba(0, 0, 0, 0.25);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

interface TabProps {
  $active: boolean;
}

const TabList = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 600px;
  width: 100%;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const Tab = styled.button<TabProps & { $showTooltip?: boolean }>`
  flex: 1;
  min-width: 140px;
  max-width: 180px;
  background: ${props =>
    props.$active
      ? 'linear-gradient(45deg, #00ffcc, #33ccff)'
      : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => (props.$active ? '#000' : props.theme.colors.text)};
  border: none;
  padding: 0.8rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  font-weight: ${props => (props.$active ? '600' : '400')};
  text-align: center;
  white-space: nowrap;

  &:hover {
    background: ${props =>
      props.$active
        ? 'linear-gradient(45deg, #00ffcc, #33ccff)'
        : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    flex: 0 0 auto;
    min-width: unset;
    max-width: unset;
    padding: 0.55rem 0.85rem;
    font-size: 0.9rem;
    border-radius: 18px;
  }
`;

const TabContent = styled.div`
  padding: 1rem 0.5rem 1rem 0.25rem;
  animation: ${fadeIn} 0.2s ease-out;
  overflow-y: auto;
  flex: 1 1 auto;
  max-height: 100%;
`;

const TabPanel = styled.div`
  & h3 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 0.75rem;
    font-size: 1.4rem;
    background: linear-gradient(45deg, #00ffcc, #33ccff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  & p {
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1rem;
    opacity: 0.9;
    font-size: 1.1rem;
    line-height: 1.6;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  & > div {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Prize bars visualization
const PrizeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0.5rem auto 1rem;
  max-width: 560px;
  width: 100%;
  /* shared column width for percent label */
  --pcw: 50px;

  @media (max-width: 480px) {
    --pcw: 20px;
    gap: 0.6rem;
  }
`;

const PrizeItem = styled.div``;

const PrizeRow = styled.div`
  display: grid;
  grid-template-columns: var(--pcw) 1fr;
  align-items: center;
  gap: 0.75rem;
`;

const Percent = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  background: linear-gradient(45deg, #00ffcc, #33ccff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: right;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const Bar = styled.div`
  height: 14px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(51,204,255,0.12);
  border-radius: 9999px;
  overflow: hidden;

  @media (max-width: 480px) {
    height: 12px;
  }
`;

const BarFill = styled.div<{ $percent: number; $active?: boolean; $delay?: number }>`
  height: 100%;
  width: ${p => (p.$active ? `${Math.min(100, Math.max(0, p.$percent))}%` : '0%')};
  background: linear-gradient(90deg, #00ffcc, #33ccff);
  box-shadow: 0 0 12px rgba(0,255,204,0.25) inset;
  transition: width 600ms ease;
  transition-delay: ${p => (p.$delay ? `${p.$delay}ms` : '0ms')};
  will-change: width;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transition-delay: 0ms;
  }
`;

const PrizeDesc = styled.div`
  font-size: 0.95rem;
  opacity: 0.85;
  margin-left: var(--pcw);
  margin-top: 0.25rem;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 2.5rem;
  animation: ${slideIn} 0.3s ease-out;
  text-align: center;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
    background: linear-gradient(45deg, #00ffcc, #33ccff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.9;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
  }

  .prize-tier {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1rem;
    margin: 1rem auto;
    max-width: 500px;
    border: 1px solid rgba(51, 204, 255, 0.1);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(51, 204, 255, 0.2);
      transform: translateY(-2px);
    }

    .percentage {
      font-size: 1.8rem;
      font-weight: bold;
      background: linear-gradient(45deg, #00ffcc, #33ccff);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.5rem;
    }

    .description {
      font-size: 1rem;
      opacity: 0.8;
    }
  }


`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin: 0.5rem 0;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    width: 100%;
    gap: 0.75rem;
  }
`;

const TrustBadgesTight = styled.div`
  width: 100%;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }

  /* TrustBadges root container */
  > div {
    margin-top: 0;
    padding: 1.25rem;
  }
`;

interface LotteryWelcomeInfoProps {
  onConnectWallet: () => void;
}

const MetaMaskModal = styled(ModalOverlay)`
  z-index: 1100;
`;

const MetaMaskContent = styled.div`
  background: rgba(0, 0, 0, 0.9);
  border-radius: 25px;
  padding: 2.5rem;
  max-width: 500px;
  width: 90%;
  text-align: center;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 0.25s ease-out;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
`;

const MetaMaskIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(45deg, #f6851b, #e2761b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(246, 133, 27, 0.3);

  img {
    width: 50px;
    height: 50px;
  }
`;

const MetaMaskTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #f6851b, #e2761b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const MetaMaskDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2rem;
  line-height: 1.6;
  font-size: 1.1rem;
  opacity: 0.9;
`;

const MetaMaskButton = styled.a`
  display: inline-block;
  background: linear-gradient(45deg, #f6851b, #e2761b);
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(246, 133, 27, 0.3);
  }
`;

export const LotteryWelcomeInfo: React.FC<LotteryWelcomeInfoProps> = ({
  onConnectWallet,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showMetaMask, setShowMetaMask] = useState(false);
  const [activeTab, setActiveTab] = useState('structure');
  const [animatePrizes, setAnimatePrizes] = useState(false);
  const [jackpot, setJackpot] = useState<string | null>(null);
  const [ticketPrice, setTicketPrice] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const endTimeRef = useRef<number | null>(null);

  const handleConnectWallet = () => {
    if (typeof window.ethereum === 'undefined') {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryWelcomeInfo] MetaMask missing');
      }
      setShowMetaMask(true);
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryWelcomeInfo] connect wallet');
      }
      onConnectWallet();
    }
  };

  // Reset animation when the panel closes so it replays on next open
  useEffect(() => {
    if (!showDetails) {
      setAnimatePrizes(false);
    }
  }, [showDetails]);

  // If the panel opens with Prizes already active, trigger the animation.
  // Also reset when switching away from Prizes while open.
  useEffect(() => {
    if (showDetails && activeTab === 'prizes') {
      requestAnimationFrame(() => setAnimatePrizes(true));
    } else if (showDetails && activeTab !== 'prizes') {
      setAnimatePrizes(false);
    }
  }, [showDetails, activeTab]);

  // Close drawer on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDetails(false);
    };
    if (showDetails) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDetails]);

  // Read-only lottery stats to improve conversion on landing
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const provider = new ethers.JsonRpcProvider(TARGET_NETWORK.rpcUrls[0]);
        const lottery = new ethers.Contract(CONTRACTS.LotteryCore, LotteryCoreABI, provider);
        const data = await lottery.getLotteryDetails();
        // data indices: [6]=pool (current round), [7]=totalPool (includes rollovers)
        const totalPool: bigint = data[7];
        const price: bigint = data[2];
        const endTime: number = Number(data[1]);
        if (!cancelled) {
          setJackpot(formatEth(totalPool));
          setTicketPrice(formatEth(price));
          endTimeRef.current = endTime;
          const now = Math.floor(Date.now() / 1000);
          setTimeRemaining(Math.max(0, endTime - now));

          if (process.env.NODE_ENV === 'development') {
            console.debug('[LotteryWelcomeInfo] landing stats', {
              jackpot: formatEth(totalPool),
              ticketPrice: formatEth(price),
              endTime,
            });
          }
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[LotteryWelcomeInfo] landing stats fetch failed', e);
        }
        // silent fail - landing still works without stats
      }
    };
    fetchStats();

    const interval = setInterval(() => {
      if (endTimeRef.current) {
        const now = Math.floor(Date.now() / 1000);
        setTimeRemaining(Math.max(0, endTimeRef.current - now));
      }
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const features = [
    {
      icon: <Ticket />,
      title: 'Instant Ticket Purchases',
      description:
        'Buy tickets instantly with our seamless purchasing process.',
    },
    {
      icon: <Sparkles />,
      title: 'Transparent Draws',
      description:
        'All draws are verifiable and transparent on the blockchain.',
    },
    {
      icon: <Shield />,
      title: 'Secure Technology',
      description:
        'Built on secure blockchain technology for maximum protection.',
    },
  ];

  return (
    <WelcomeContainer>
      <ScrollableContent>
        <Kicker>
          Arbitrum One
          <span>•</span>
          On-Chain
          <span>•</span>
          Chainlink VRF
        </Kicker>
        <WelcomeTitle>Arbitrum Lottery</WelcomeTitle>

        <WelcomeDescription>
          Experience the excitement of our decentralized lottery on Arbitrum.
          Win big while enjoying a transparent and fair gaming experience.
        </WelcomeDescription>

        <HeroStats>
          <StatCard>
            <div className="label">Total Pool</div>
            <div className="value">{jackpot ?? '—'}</div>
          </StatCard>
          <StatCard>
            <div className="label">Ticket Price</div>
            <div className="value">{ticketPrice ?? '—'}</div>
          </StatCard>
          <StatCard>
            <div className="label">Time Remaining</div>
            <div className="value">
              {(() => {
                const h = Math.floor(timeRemaining / 3600)
                  .toString()
                  .padStart(2, '0');
                const m = Math.floor((timeRemaining % 3600) / 60)
                  .toString()
                  .padStart(2, '0');
                const s = Math.floor(timeRemaining % 60)
                  .toString()
                  .padStart(2, '0');
                return `${h}:${m}:${s}`;
              })()}
            </div>
          </StatCard>
        </HeroStats>

        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} delay={0.2 * (index + 1)}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>

        <ButtonContainer>
          <ActionButton variant="primary" onClick={handleConnectWallet}>
            Connect Wallet to Play
          </ActionButton>
          <ActionButton variant="default" onClick={() => setShowDetails(!showDetails)}>
            Learn More
          </ActionButton>
        </ButtonContainer>

        <TrustBadgesTight>
          <TrustBadges />
        </TrustBadgesTight>
      </ScrollableContent>

      {/* Drawer overlay (click to close). Kept light without blur. */}
      <ModalOverlay
        aria-hidden={!showDetails}
        role="presentation"
        $isVisible={showDetails}
        onClick={() => setShowDetails(false)}
      />

      {/* Slide-over drawer content */}
      <InfoDrawer
        role="dialog"
        aria-modal="true"
        aria-label="Learn more about the lottery"
        onClick={e => e.stopPropagation()}
        $isVisible={showDetails}
      >
        <DrawerHeader>
          <HeaderSpacer aria-hidden="true" />
          <DrawerTitle>About the Lottery</DrawerTitle>
          <CloseButton onClick={() => setShowDetails(false)} aria-label="Close">
            <span>✕</span>
          </CloseButton>
        </DrawerHeader>

        <DrawerBody>
          <TabContainer>
            <TabList>
              <Tab
                $active={activeTab === 'structure'}
                onClick={() => {
                  setActiveTab('structure');
                }}
              >
                Structure
              </Tab>
              <Tab
                $active={activeTab === 'prizes'}
                onClick={() => {
                  setActiveTab('prizes');
                  // trigger animation when opening prizes
                  // small timeout ensures layout is ready
                  requestAnimationFrame(() => setAnimatePrizes(true));
                }}
              >
                Prizes
              </Tab>
              <Tab
                $active={activeTab === 'how'}
                onClick={() => {
                  setActiveTab('how');
                }}
              >
                How to Play
              </Tab>
            </TabList>
          </TabContainer>

          <TabContent>
            {activeTab === 'structure' && (
              <TabPanel>
                <ContentSection>
                  <h3>🎯 Multiple Winning Tiers</h3>
                  <p>
                    Match more numbers for bigger prizes. Our lottery features
                    multiple winning tiers to increase your chances of winning.
                    The more numbers you match, the bigger your prize!
                  </p>
                </ContentSection>
                <ContentSection>
                  <h3>🎰 Jackpot Rollovers</h3>
                  <p>
                    Unclaimed prizes increase future jackpots, making each
                    subsequent draw even more exciting! Watch the prize pool grow
                    with each rollover.
                  </p>
                </ContentSection>
                <ContentSection>
                  <h3>🔒 Smart Contract Security</h3>
                  <p>
                    Our lottery runs on secure smart contracts with verifiable
                    randomness, ensuring complete transparency and fairness for all
                    participants.
                  </p>
                </ContentSection>
              </TabPanel>
            )}
            {activeTab === 'prizes' && (
              <TabPanel>
                <ContentSection>
                  <h3>🏆 Prize Distribution</h3>
                  <PrizeList>
                    {[
                      { percent: 80, label: 'Jackpot Winner (Match 7 Numbers)' },
                      { percent: 10, label: 'Second Tier (Match 6 Numbers)' },
                      { percent: 5, label: 'Third Tier (Match 5 Numbers)' },
                      { percent: 3, label: 'Fourth Tier (Match 4 Numbers)' },
                    ].map((tier, i) => (
                      <PrizeItem key={tier.label}>
                        <PrizeRow>
                          <Percent>{tier.percent}%</Percent>
                          <Bar>
                            <BarFill
                              $percent={tier.percent}
                              $active={animatePrizes && activeTab === 'prizes'}
                              $delay={i * 80}
                            />
                          </Bar>
                        </PrizeRow>
                        <PrizeDesc>{tier.label}</PrizeDesc>
                      </PrizeItem>
                    ))}
                  </PrizeList>
                </ContentSection>
                <ContentSection>
                  <h3>💰 Platform Fee</h3>
                  <p>
                    Only 2% platform fee to cover costs of VRF (Verifiable Random Function) and Chainlink automation. These systems ensure fair, transparent random number generation and reliable automated draws.
                  </p>
                </ContentSection>
              </TabPanel>
            )}
            {activeTab === 'how' && (
              <TabPanel>
                <ContentSection>
                  <h3>📱 1. Connect Your Wallet</h3>
                  <p>Use your Arbitrum wallet to participate in the lottery</p>
                </ContentSection>
                <ContentSection>
                  <h3>🎯 2. Choose Your Numbers</h3>
                  <p>Pick 7 lucky numbers between 1 and 45</p>
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    flexWrap: 'wrap', 
                    margin: '1rem 0',
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '1rem',
                    borderRadius: '10px',
                    justifyContent: 'center',
                  }}>
                    {[7, 13, 21, 28, 33, 39, 42].map((num) => (
                      <div key={num} style={{
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(45deg, #00ffcc, #33ccff)',
                        borderRadius: '50%',
                        color: '#000',
                        fontWeight: 'bold'
                      }}>{num}</div>
                    ))}
                  </div>
                </ContentSection>
                <ContentSection>
                  <h3>🎫 3. Buy Your Tickets</h3>
                  <p>Purchase tickets to enter the current lottery round</p>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '1rem',
                    borderRadius: '10px',
                    margin: '1rem 0',
                    border: '1px dashed rgba(255, 255, 255, 0.2)'
                  }}>
                    <p style={{ fontSize: '0.9rem', opacity: 0.7, textAlign: 'center' }}>Sample Ticket</p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      flexWrap: 'wrap',
                      marginTop: '0.5rem',
                      justifyContent: 'center'
                    }}>
                      {[7, 13, 21, 28, 33, 39, 42].map((num) => (
                        <span key={num} style={{ opacity: 0.8 }}>{num}</span>
                      ))}
                    </div>
                  </div>
                </ContentSection>
                <ContentSection>
                  <h3>⏰ 4. Wait for the Draw</h3>
                  <p>
                    Draws occur every 24 hours - check back to see if you've
                    won!
                  </p>
                </ContentSection>
              </TabPanel>
            )}
          </TabContent>
        </DrawerBody>
      </InfoDrawer>

      <MetaMaskModal
        $isVisible={showMetaMask}
        onClick={() => setShowMetaMask(false)}
      >
        <MetaMaskContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={() => setShowMetaMask(false)}>
            <span>✕</span>
          </CloseButton>
          <MetaMaskIcon>
            <img
              src="https://raw.githubusercontent.com/MetaMask/brand-resources/e9cddbf787c62bb387f9b586e7433ab6be440206/SVG/SVG_MetaMask_Icon_Color.svg"
              alt="MetaMask"
            />
          </MetaMaskIcon>
          <MetaMaskTitle>MetaMask Required</MetaMaskTitle>
          <MetaMaskDescription>
            To participate in the Arbitrum Lottery, you'll need to install
            MetaMask - a secure wallet for accessing Web3 applications. MetaMask
            allows you to safely connect to our lottery and manage your tickets.
          </MetaMaskDescription>
          <MetaMaskButton
            href="https://metamask.io/download/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Install MetaMask
          </MetaMaskButton>
        </MetaMaskContent>
      </MetaMaskModal>
    </WelcomeContainer>
  );
};

export default LotteryWelcomeInfo;
